import dayjs from "dayjs";
import { dateFormat, getTeamLogo } from "@/lib/utils";
import { type PlayerData } from "@/api/player";
import { PlayerAvatar } from "../common/PlayerAvatar";
import { Loader } from "../common/loader";

interface PlayerBannerProps {
    playerId: number;
    playerData: PlayerData | undefined;
    isLoading: boolean;
}

export function PlayerBanner({ playerId, playerData, isLoading }: PlayerBannerProps) {
    const allKpis = [...(playerData?.kpis ?? []), ...(playerData?.goalkeeperKpis ?? [])];
    const lastKpi = allKpis.sort((a, b) => new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime())[0];

    let lastMatchLabel: string | null = null;
    let lastMatchOpponent: string | null = null;
    if (lastKpi && playerData) {
        const isHome =
            playerData.teamId !== undefined && (lastKpi.homeTeamId !== undefined || lastKpi.awayTeamId !== undefined)
                ? playerData.teamId === lastKpi.homeTeamId
                : lastKpi.homeTeam === " ";
        const opponent = isHome ? lastKpi.awayTeam : lastKpi.homeTeam;
        lastMatchOpponent = opponent;
        lastMatchLabel = `vs ${opponent} · ${dayjs(lastKpi.matchDate).format(dateFormat)}`;
    }

    const opponentLogo = getTeamLogo(lastMatchOpponent ?? undefined);

    return (
        <div className="relative rounded-xl overflow-hidden flex items-center min-h-[110px] md:min-h-[140px] performance-gradient">
            <div className="flex items-center pl-4 shrink-0">
                <div
                    className="w-20 h-20 md:w-28 md:h-28 rounded-lg overflow-hidden shadow-xl shrink-0"
                    style={{ border: "2px solid #ba0a06" }}
                >
                    <PlayerAvatar
                        playerId={playerId}
                        teamId={playerData?.teamId}
                        firstName={playerData?.firstName}
                        lastName={playerData?.lastName}
                        variant="dark"
                    />
                </div>
            </div>

            <div className="relative z-10 flex flex-col justify-center flex-1 min-w-0 px-4 md:px-8 py-4">
                {isLoading ? (
                    <Loader message="" className="text-sm" />
                ) : (
                    <>
                        <h1 className="font-headline font-black text-white tracking-tighter leading-tight text-2xl md:text-4xl">
                            {playerData ? `${playerData.firstName} ${playerData.lastName}` : "—"}
                        </h1>
                        <div className="flex flex-wrap items-center gap-3 mt-1.5">
                            {playerData?.role && playerData.role.length > 0 && (
                                <span className="bg-[#ba0a06] text-white text-[10px] font-black px-2 py-0.5 rounded-sm tracking-widest uppercase">
                                    {playerData.role.join("/")}
                                </span>
                            )}
                            {lastMatchLabel && lastKpi && (
                                <>
                                    <span className="md:hidden flex items-center gap-1.5 text-blue-200/70 font-label text-xs uppercase tracking-widest">
                                        {opponentLogo ? (
                                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-white/90 shrink-0">
                                                <img
                                                    src={opponentLogo}
                                                    alt={lastMatchOpponent ?? ""}
                                                    className="w-4 h-4 object-contain"
                                                />
                                            </span>
                                        ) : (
                                            <span className="truncate max-w-20">vs {lastMatchOpponent}</span>
                                        )}
                                        <span className="shrink-0">{dayjs(lastKpi.matchDate).format(dateFormat)}</span>
                                    </span>
                                    <span className="hidden md:inline text-blue-200/70 font-label text-xs uppercase tracking-widest">
                                        {lastMatchLabel}
                                    </span>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className="relative z-10 hidden md:flex items-center md:px-10 shrink-0">
                <span
                    className="font-headline font-black text-white/40 leading-none select-none"
                    style={{ fontSize: "clamp(3rem, 6vw, 5rem)" }}
                >
                    {playerData?.jerseyNumber ?? ""}
                </span>
            </div>
        </div>
    );
}
